import { useRef } from "react";
import { X, Copy, Download, Flame, Link2 } from "lucide-react";
import { toast } from "sonner";

export const ShareCardModal = ({ open, onClose, question, answer, shareId }) => {
  const cardRef = useRef(null);

  if (!open) return null;

  const shareUrl = shareId ? `${window.location.origin}/a/${shareId}` : null;

  // Grab a beautiful pull-quote from the answer — first blockquote if any, else first paragraph.
  const bqMatch = answer && answer.match(/>\s*"([^"]+)"[\s\S]*?—\s*\*([^*]+)\*/);
  const pullQuote = bqMatch ? bqMatch[1].trim() : null;
  const pullCite = bqMatch ? bqMatch[2].trim() : null;
  const firstPara = !pullQuote && answer
    ? (answer.split(/\n\n/).find((p) => !p.startsWith("#") && !p.startsWith(">") && p.trim().length > 20) || "").slice(0, 260)
    : null;

  const copyLink = async () => {
    if (!shareUrl) {
      toast.error("Link not ready yet. Try again in a moment.");
      return;
    }
    try {
      if (navigator.share) {
        try {
          await navigator.share({
            title: "Ask God — a reflection",
            text: question,
            url: shareUrl,
          });
          return;
        } catch {
          /* user cancelled — fall through to clipboard */
        }
      }
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied to clipboard");
    } catch {
      toast.error("Could not copy. Please try again.");
    }
  };

  const copyText = async () => {
    const base = pullQuote
      ? `"${pullQuote}"\n— ${pullCite}\n\nAsked: ${question}`
      : `${firstPara}\n\nAsked: ${question}`;
    const text = shareUrl ? `${base}\n\nRead the full reflection: ${shareUrl}\nvia Ask God` : `${base}\n\nvia Ask God`;
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const downloadPng = async () => {
    if (!cardRef.current) return;
    // Render the card node into a canvas via foreignObject SVG trick.
    const node = cardRef.current;
    const rect = node.getBoundingClientRect();
    const width = Math.ceil(rect.width);
    const height = Math.ceil(rect.height);

    // Inline the styles by cloning
    const clone = node.cloneNode(true);
    clone.style.margin = "0";
    const serializer = new XMLSerializer();
    const html = serializer.serializeToString(clone);

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
        <foreignObject width="100%" height="100%">
          <div xmlns="http://www.w3.org/1999/xhtml" style="width:${width}px;height:${height}px;">
            ${html}
          </div>
        </foreignObject>
      </svg>`;

    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * 2;
      canvas.height = height * 2;
      const ctx = canvas.getContext("2d");
      ctx.scale(2, 2);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((pngBlob) => {
        if (!pngBlob) {
          toast.error("Download failed — try Copy instead.");
          return;
        }
        const a = document.createElement("a");
        a.href = URL.createObjectURL(pngBlob);
        a.download = "ask-god-card.png";
        a.click();
        URL.revokeObjectURL(a.href);
      });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      toast.error("Download failed — try Copy instead.");
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#FFFFFF]/50 backdrop-blur-sm fade-up"
      onClick={onClose}
      data-testid="share-card-modal-backdrop"
    >
      <div
        data-testid="share-card-modal"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-xl"
      >
        <button
          onClick={onClose}
          data-testid="share-card-close-btn"
          className="absolute -top-3 -right-3 bg-[#5C0E1D] rounded-full p-1.5 gold-border text-[#E7B04A] hover:bg-[#4A0A17] z-10"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div
          ref={cardRef}
          className="rounded-2xl bg-[#5C0E1D] gold-border-strong p-8 sm:p-10"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          <div className="flex items-center gap-2 text-[#E7B04A] mb-3">
            <Flame className="w-4 h-4" strokeWidth={1.6} />
            <span className="text-xs sm:text-[0.75rem] uppercase tracking-[0.2em] font-bold">
              Ask God
            </span>
          </div>

          {pullQuote ? (
            <>
              <blockquote
                className="font-scripture italic text-xl sm:text-2xl leading-relaxed text-[#FFFFFF]"
                style={{ fontFamily: "'EB Garamond', Georgia, serif" }}
              >
                &ldquo;{pullQuote}&rdquo;
              </blockquote>
              <div className="mt-4 text-sm text-[#E7B04A] font-bold">— {pullCite}</div>
            </>
          ) : (
            <p
              className="text-base text-[#FFFFFF] leading-relaxed"
              style={{ fontFamily: "'EB Garamond', Georgia, serif" }}
            >
              {firstPara}
            </p>
          )}

          <div className="mt-6 pt-4 border-t border-[#E7B04A]/30">
            <div className="text-xs sm:text-[0.75rem] uppercase tracking-[0.16em] text-[#FBEBD3]">
              Question
            </div>
            <div
              className="mt-1 text-sm text-[#FFFFFF]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {question}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          <button
            data-testid="copy-share-link-btn"
            onClick={copyLink}
            disabled={!shareUrl}
            className="inline-flex items-center gap-2 rounded-full bg-[#E7B04A] px-5 py-2.5 text-sm font-bold uppercase tracking-[0.12em] text-[#5C0E1D] hover:bg-[#F5C76B] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
          >
            <Link2 className="w-4 h-4" strokeWidth={2.2} />
            {shareUrl ? "Copy share link" : "Preparing link…"}
          </button>
          <button
            data-testid="copy-quote-btn"
            onClick={copyText}
            className="inline-flex items-center gap-2 rounded-full bg-[#5C0E1D] px-4 py-2 gold-border text-sm text-[#FFFFFF] hover:bg-[#4A0A17] transition-colors"
          >
            <Copy className="w-4 h-4" /> Copy quote
          </button>
          <button
            data-testid="download-card-btn"
            onClick={downloadPng}
            className="inline-flex items-center gap-2 rounded-full bg-[#5C0E1D] gold-border px-4 py-2 text-sm text-[#FFFFFF] hover:bg-[#4A0A17] transition-colors"
          >
            <Download className="w-4 h-4" /> PNG
          </button>
        </div>
        {shareUrl && (
          <p
            data-testid="share-link-preview"
            className="mt-3 text-center text-[0.72rem] text-[#FBEBD3] break-all font-sans-clean"
          >
            {shareUrl}
          </p>
        )}
      </div>
    </div>
  );
};

export default ShareCardModal;
